import { useMemo, useState } from "react";
import {
  useAdminListRateLimitAlerts,
  getAdminListRateLimitAlertsQueryKey,
  useGetMe,
  adminListRateLimitAlerts,
  type AdminListRateLimitAlertsParams,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Loader2,
  ShieldAlert,
  ChevronLeft,
  ChevronRight,
  Download,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";

const PAGE_SIZE = 50;

const CATEGORY_OPTIONS = [
  { value: "__all__", label: "All categories" },
  { value: "pubsub.rate_limited_ip", label: "IP flood" },
  { value: "pubsub.rate_limited_msgid", label: "messageId replay" },
] as const;

function localInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

// Convert an ISO timestamp (as embedded in alert deep links) into the
// `YYYY-MM-DDTHH:mm` format the <input type="datetime-local"> needs.
// The browser interprets that string as local time, so we project the
// ISO instant into the user's local zone. Returns "" for empty/invalid
// input so the filter stays untouched.
function isoToLocalInput(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// One-shot read of the URL search string at module load time. Lets
// rate-limit alert notification emails pre-populate the filter inputs
// (category, since, until) without forcing the page to subscribe to
// URL changes — once the user starts editing, their state takes over.
function readInitialFiltersFromUrl(): {
  category: string;
  since: string;
  until: string;
} {
  if (typeof window === "undefined") {
    return { category: "__all__", since: "", until: "" };
  }
  const params = new URLSearchParams(window.location.search);
  const categoryRaw = (params.get("category") ?? "").trim();
  // Only honor recognized categories so a malformed link can't pin a
  // bogus value into the Select that the user can't undo cleanly.
  const allowed = CATEGORY_OPTIONS.map((o) => o.value) as readonly string[];
  const category = allowed.includes(categoryRaw) ? categoryRaw : "__all__";
  return {
    category,
    since: isoToLocalInput(params.get("since")),
    until: isoToLocalInput(params.get("until")),
  };
}

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function categoryLabel(category: string): string {
  if (category === "pubsub.rate_limited_ip") return "IP flood";
  if (category === "pubsub.rate_limited_msgid") return "messageId replay";
  return category;
}

function categoryVariant(category: string): "destructive" | "secondary" {
  if (category === "pubsub.rate_limited_ip") return "destructive";
  return "secondary";
}

function csvEscape(value: string | number): string {
  const str = String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

type ExportAlert = {
  id: number;
  category: string;
  count: number;
  threshold: number;
  topIps: string[];
  topMessageIds: string[];
  windowStart: string;
  windowEnd: string;
  createdAt: string;
};

function alertsToCsv(alerts: ExportAlert[]): string {
  const headers = [
    "category",
    "count",
    "threshold",
    "top_ips",
    "top_message_ids",
    "window_start",
    "window_end",
    "fired_at",
  ];
  const lines = [headers.join(",")];
  for (const a of alerts) {
    lines.push(
      [
        csvEscape(a.category),
        csvEscape(a.count),
        csvEscape(a.threshold),
        csvEscape(a.topIps.join("; ")),
        csvEscape(a.topMessageIds.join("; ")),
        csvEscape(a.windowStart),
        csvEscape(a.windowEnd),
        csvEscape(a.createdAt),
      ].join(","),
    );
  }
  return lines.join("\n");
}

function downloadBlob(filename: string, content: string, mime: string) {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const EXPORT_PAGE_SIZE = 200;

export default function AdminRateLimitAlerts() {
  const [, navigate] = useLocation();
  const { data: me } = useGetMe();
  // Lazy initializers so the URL is read once at mount. The inputs
  // become controlled by user state immediately after — we never
  // re-sync from the URL, so reloading clears any deep-link pre-fill
  // that the user has since edited.
  const initialFilters = useMemo(readInitialFiltersFromUrl, []);
  const [page, setPage] = useState(0);
  const [category, setCategory] = useState(initialFilters.category);
  const [since, setSince] = useState(initialFilters.since);
  const [until, setUntil] = useState(initialFilters.until);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFetched, setExportFetched] = useState(0);
  const [exportTotal, setExportTotal] = useState<number | null>(null);

  const params = useMemo<AdminListRateLimitAlertsParams>(() => {
    const out: AdminListRateLimitAlertsParams = {
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
    if (category !== "__all__") out.category = category;
    const sinceIso = localInputToIso(since);
    if (sinceIso) out.since = sinceIso;
    const untilIso = localInputToIso(until);
    if (untilIso) out.until = untilIso;
    return out;
  }, [page, category, since, until]);

  const { data, isLoading, isFetching, error } = useAdminListRateLimitAlerts(params, {
    query: {
      queryKey: getAdminListRateLimitAlertsQueryKey(params),
      refetchOnWindowFocus: false,
    },
  });

  if (me && !me.isAdmin) {
    navigate("/app");
    return null;
  }

  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const alerts = data?.alerts ?? [];

  const hasActiveFilters = category !== "__all__" || since !== "" || until !== "";

  const resetFilters = () => {
    setCategory("__all__");
    setSince("");
    setUntil("");
    setPage(0);
  };

  const handleExport = async (format: "csv" | "json") => {
    setIsExporting(true);
    setExportFetched(0);
    // Seed with the currently-known total so the indicator can show
    // "of N" immediately, before the first export page returns.
    setExportTotal(typeof totalCount === "number" ? totalCount : null);
    try {
      const baseParams: AdminListRateLimitAlertsParams = {};
      if (category !== "__all__") baseParams.category = category;
      const sinceIso = localInputToIso(since);
      if (sinceIso) baseParams.since = sinceIso;
      const untilIso = localInputToIso(until);
      if (untilIso) baseParams.until = untilIso;

      const all: ExportAlert[] = [];
      let offset = 0;
      const safetyCap = 50000;
      while (all.length < safetyCap) {
        const resp = await adminListRateLimitAlerts({
          ...baseParams,
          limit: EXPORT_PAGE_SIZE,
          offset,
        });
        const batch = resp.alerts ?? [];
        all.push(...batch);
        if (typeof resp.totalCount === "number") {
          setExportTotal(resp.totalCount);
        }
        setExportFetched(all.length);
        if (batch.length < EXPORT_PAGE_SIZE) break;
        if (typeof resp.totalCount === "number" && all.length >= resp.totalCount) {
          break;
        }
        offset += EXPORT_PAGE_SIZE;
      }

      if (all.length === 0) {
        toast.info("No alerts to export.");
        return;
      }

      const stamp = new Date().toISOString().replace(/[:.]/g, "-");
      if (format === "csv") {
        const csv = alertsToCsv(all);
        downloadBlob(`rate-limit-alerts-${stamp}.csv`, csv, "text/csv");
      } else {
        const json = JSON.stringify(all, null, 2);
        downloadBlob(
          `rate-limit-alerts-${stamp}.json`,
          json,
          "application/json",
        );
      }
      toast.success(`Exported ${all.length} alert${all.length === 1 ? "" : "s"}.`);
    } catch (err) {
      console.error("Failed to export rate-limit alerts", err);
      toast.error("Failed to export alerts.");
    } finally {
      setIsExporting(false);
      setExportFetched(0);
      setExportTotal(null);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldAlert className="h-6 w-6 text-destructive" />
          Rate-Limit Alerts
        </h1>
        <p className="text-muted-foreground mt-1">
          History of Pub/Sub rate-limit burst alerts detected by the audit scanner.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label htmlFor="rl-category">Category</Label>
              <Select
                value={category}
                onValueChange={(val) => {
                  setCategory(val);
                  setPage(0);
                }}
              >
                <SelectTrigger id="rl-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rl-since">Since</Label>
              <Input
                id="rl-since"
                type="datetime-local"
                value={since}
                onChange={(e) => {
                  setSince(e.target.value);
                  setPage(0);
                }}
              />
            </div>
            <div>
              <Label htmlFor="rl-until">Until</Label>
              <Input
                id="rl-until"
                type="datetime-local"
                value={until}
                onChange={(e) => {
                  setUntil(e.target.value);
                  setPage(0);
                }}
              />
            </div>
          </div>
          {hasActiveFilters && (
            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" onClick={resetFilters}>
                Reset filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div>
            <CardTitle>Alert History</CardTitle>
            <CardDescription>
              {totalCount === 0
                ? "No rate-limit alerts match the current filters."
                : `${totalCount} alert${totalCount === 1 ? "" : "s"} total`}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {isFetching && !isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : null}
            {isExporting ? (
              <span
                className="text-xs text-muted-foreground tabular-nums"
                data-testid="text-export-progress"
                aria-live="polite"
              >
                Exporting {exportFetched.toLocaleString()}
                {exportTotal !== null
                  ? ` of ${exportTotal.toLocaleString()}`
                  : ""}
                …
              </span>
            ) : null}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isExporting || isLoading || totalCount === 0}
                  data-testid="button-export-rate-limit-alerts"
                >
                  {isExporting ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-1" />
                  )}
                  Export
                  <ChevronDown className="h-4 w-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => handleExport("csv")}
                  data-testid="menuitem-export-rate-limit-alerts-csv"
                >
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleExport("json")}
                  data-testid="menuitem-export-rate-limit-alerts-json"
                >
                  Export as JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="text-destructive text-sm py-4">
              Failed to load alerts.
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-muted-foreground text-sm py-8 text-center">
              No rate-limit alerts found.
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Count</TableHead>
                      <TableHead>Top IPs</TableHead>
                      <TableHead>Window</TableHead>
                      <TableHead>Fired At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map((alert) => (
                      <TableRow key={alert.id}>
                        <TableCell>
                          <Badge variant={categoryVariant(alert.category)}>
                            {categoryLabel(alert.category)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {alert.count}
                          <span className="text-muted-foreground text-xs ml-1">
                            / {alert.threshold}
                          </span>
                        </TableCell>
                        <TableCell>
                          {alert.topIps.length > 0 ? (
                            <div className="space-y-0.5">
                              {alert.topIps.slice(0, 3).map((ip) => (
                                <div
                                  key={ip}
                                  className="text-xs font-mono text-muted-foreground"
                                >
                                  {ip}
                                </div>
                              ))}
                              {alert.topIps.length > 3 && (
                                <div className="text-xs text-muted-foreground">
                                  +{alert.topIps.length - 3} more
                                </div>
                              )}
                            </div>
                          ) : alert.topMessageIds.length > 0 ? (
                            <div className="space-y-0.5">
                              {alert.topMessageIds.slice(0, 2).map((id) => (
                                <div
                                  key={id}
                                  className="text-xs font-mono text-muted-foreground truncate max-w-[200px]"
                                  title={id}
                                >
                                  {id}
                                </div>
                              ))}
                              {alert.topMessageIds.length > 2 && (
                                <div className="text-xs text-muted-foreground">
                                  +{alert.topMessageIds.length - 2} more
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              —
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {fmtTimestamp(alert.windowStart)}
                          <br />
                          → {fmtTimestamp(alert.windowEnd)}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {fmtTimestamp(alert.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <span className="text-sm text-muted-foreground">
                    Page {page + 1} of {totalPages}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
